import { mxn, fdate, boolText } from "@/lib/format";
import { StatusBadge, RiskBadge } from "@/components/common/StatusBadge";
import { labelTipoPersona } from "@/lib/catalogos_maestros";
import { Inbox, Loader2, AlertTriangle } from "lucide-react";

export function renderCell(col, row) {
  if (typeof col.render === "function") return col.render(row);
  // `value` permite mostrar una etiqueta legible conservando el estilo de la columna
  // (p. ej. el estatus de la solicitud: «En análisis» se pinta «Enviada al Comité»).
  const v = typeof col.value === "function" ? col.value(row) : row[col.key];
  switch (col.kind) {
    case "currency":
      return <span className="font-mono tabular-nums">{mxn(v)}</span>;
    case "currency_opcional":
      // sep-2026 · Un monto que todavía no se capturó NO es $0.00: se muestra «—»
      // (p. ej. el autorizado de una solicitud que aún no pasa por el Comité).
      return (
        <span className="font-mono tabular-nums">
          {v === null || v === undefined || v === "" ? "—" : mxn(v)}
        </span>
      );
    case "date":
      return <span className="text-slate-500">{fdate(v)}</span>;
    case "bool":
      return <StatusBadge value={boolText(v)} />;
    case "badge":
      return <StatusBadge value={v} />;
    case "tipo_persona":
      return <StatusBadge value={labelTipoPersona(v)} />;
    case "risk":
      return <RiskBadge value={v} />;
    default:
      return (
        <span className="block truncate" title={v === undefined || v === null || v === "" ? "" : String(v)}>
          {v === undefined || v === null || v === "" ? "—" : String(v)}
        </span>
      );
  }
}

export function DataTable({ columns, rows, actions, onRowClick, emptyLabel = "Sin registros", testid, loading = false, error = false, onRetry }) {
  return (
    <div data-testid={testid} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {c.label}
                </th>
              ))}
              {actions && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-sm">Cargando…</span>
                  </div>
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <AlertTriangle className="h-6 w-6" />
                    <span className="text-sm">No se pudo cargar la información.</span>
                    <button type="button" onClick={onRetry} className="text-sm font-medium text-primary underline">Reintentar</button>
                  </div>
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <Inbox className="h-8 w-8" strokeWidth={1.5} />
                    <span className="text-sm">{emptyLabel}</span>
                  </div>
                </td>
              </tr>
            )}
            {!error && rows.map((row, i) => (
              <tr
                key={row.id || i}
                onClick={() => onRowClick && onRowClick(row)}
                className={`border-b border-slate-100 text-sm text-slate-700 transition-colors hover:bg-slate-50/70 ${
                  onRowClick ? "cursor-pointer" : ""
                }`}
              >
                {columns.map((c) => (
                  <td key={c.key} className="max-w-[260px] overflow-hidden px-4 py-3">
                    {renderCell(c, row)}
                  </td>
                ))}
                {actions && (
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
